import { NotificationIdState } from '../../utilities/notification-id.type';
import { ODataCalibrationModel } from '../../../models/low-level/measurement-tool.model';
import { createAction, props } from '@ngrx/store';
import { ConfirmationTimesheetContext } from '../../utilities/confirmation-timesheet-context';
import { ConfirmationIdentityState, ConfirmationState } from '../confirmation-collection.state';

// ---- Lifecycle ----------------------------------------------------------------------------

/**
 * Loads the stored confirmations into the slice. `ConfirmationPersistenceEffects` dispatches it for
 * itself through `ngrxOnInitEffects`, so it lands after that class's effects are subscribed — the
 * only ordering under which the hydrate effect can see it.
 */
export const initConfirmations = createAction('[Confirmation] Init');

// The storage read has no action of its own: the generic persistence layer's `sliceHydrated` carries
// the result, and `hydrateConfirmationsReducer` settles the slice from it directly.

// ---- Writes ---------------------------------------------------------------------------------

/**
 * Store the confirmation, replacing the one already stored for the same notification and type.
 * Triggers persistence.
 *
 * `correlationId` lets the facade await that specific write: `ConfirmationPersistenceEffects` carries
 * it through to the generic layer's `persistSlice`, which echoes it back on
 * `slicePersisted`/`persistenceError` — the outcomes `saveConfirmation` settles on, so a caller learns
 * the confirmation reached storage rather than only the slice.
 */
export const upsertConfirmation = createAction(
    '[Confirmation] Upsert',
    props<{ confirmation: ConfirmationState; correlationId: string }>(),
);

/**
 * The writes a save owes beyond the slice itself — the materials' local copy and the service report's
 * recipients — have finished for the save identified by `correlationId`.
 *
 * A save is only done when both this and the slice's own `slicePersisted` have arrived, which is what
 * `saveConfirmation` waits for: the two run in parallel and settle independently, so neither can stand
 * in for the other.
 */
export const confirmationSideEffectsSettled = createAction(
    '[Confirmation] Save side effects settled',
    props<{ correlationId: string }>(),
);

/** One of those writes failed; the save reports the failure rather than completing. */
export const confirmationSideEffectsFailed = createAction(
    '[Confirmation] Save side effects failed',
    props<{ correlationId: string; error: string }>(),
);

/**
 * Remove the stored confirmation for a notification/type pair, e.g. after a successful submit or a
 * completed-elsewhere cleanup. Triggers persistence.
 *
 * `correlationId` names this removal's write exactly as {@link upsertConfirmation}'s does:
 * `ConfirmationPersistenceEffects` carries it through to `persistSlice`, which echoes it back on
 * `slicePersisted`/`persistenceError`, so a caller can await the removal reaching storage rather than
 * only the slice. No caller awaits it today — the submit pipeline settles on its own outcome actions —
 * but every write naming itself keeps the id meaningful in the trail and leaves that option open.
 */
export const removeConfirmation = createAction(
    '[Confirmation] Remove',
    props<ConfirmationIdentityState & { correlationId: string }>(),
);

/**
 * Discard confirmations whose notification is no longer present. `selectStaleConfirmations` decides
 * which those are, across the confirmation and notification slices; the answer travels on the action
 * because a reducer sees only its own slice and so cannot ask the notification one. That is why this
 * keeps props where the no-props cleanup convention used elsewhere in this migration
 * (e.g. `removeExpiredCompletedNotifications`) does not.
 *
 * `correlationId` names the purge's write the same way {@link removeConfirmation}'s does. The purge is
 * dispatched by an effect rather than by a facade, so it mints its own id.
 */
export const purgeStaleConfirmations = createAction(
    '[Confirmation] Purge stale',
    props<{ stale: ConfirmationIdentityState[]; correlationId: string }>(),
);

// ---- Submit -----------------------------------------------------------------------------------

/**
 * Kick off the submit pipeline for `confirmation` (a full snapshot taken from the live
 * `ConfirmationModel` at the moment `submitConfirmation()` was called, not a store lookup — the
 * technician may submit edits that were never explicitly saved). `correlationId` lets the facade await
 * this specific submission's outcome.
 *
 * The timesheet slice also reduces this action, committing the confirmation's time recording as its
 * work entries — which is why `timesheet` rides along. A reducer sees only its own slice, so the
 * customer, address and config switches it would otherwise have to look up are resolved at the
 * dispatch site, the same way `purgeStaleConfirmations` carries its answer.
 */
export const submitConfirmationRequested = createAction(
    '[Confirmation] Submit requested',
    props<{
        confirmation: ConfirmationState;
        timesheet: ConfirmationTimesheetContext;
        correlationId: string;
    }>(),
);

/** The submit pipeline completed successfully; the confirmation has already been removed via `removeConfirmation`. */
export const submitConfirmationSucceeded = createAction(
    '[Confirmation] Submit succeeded',
    props<ConfirmationIdentityState & { correlationId: string }>(),
);

/**
 * The submit was abandoned before anything was sent, because the notification is already in the
 * recently-completed list (typically completed on another device and synced in). Mirrors the legacy
 * `submitConfirmation`, which began with `filterConfirmationStatusComplete()` — an rxjs `filter`, so
 * an already-completed notification made the whole pipeline complete without emitting rather than
 * submit a duplicate to SAP. Distinct from {@link submitConfirmationFailed}: nothing went wrong, so
 * the facade completes its caller silently instead of erroring.
 */
export const submitConfirmationAborted = createAction(
    '[Confirmation] Submit aborted (already completed)',
    props<ConfirmationIdentityState & { correlationId: string }>(),
);

/** The submit pipeline failed at some step; the confirmation is left in place so the technician can retry. */
export const submitConfirmationFailed = createAction(
    '[Confirmation] Submit failed',
    props<ConfirmationIdentityState & { correlationId: string; error: string }>(),
);

// ---- Signals ------------------------------------------------------------------------------

/** Replaces the `Subject` the legacy service used: signals that the current confirmation was already completed elsewhere. */
export const confirmationCompletedPreviously = createAction('[Confirmation] Completed previously');

/**
 * A confirmation screen was opened for `notificationId`. The submit effects' `confirmationOpened$`
 * ensures start-of-work is set and marks the notification as started — side effects the legacy
 * `getConfirmation` used to run inline.
 */
export const confirmationOpened = createAction(
    '[Confirmation] Opened',
    props<{ notificationId: NotificationIdState }>(),
);

/** Defer signing: move the notification's status forward so the technician can sign it later. */
export const signLater = createAction('[Confirmation] Sign later', props<{ notificationId: NotificationIdState }>());

/**
 * Reconcile a confirmation that was completed on another device: save its CRM status (when the
 * notification is still known) and drop the local copy, addressed by the
 * `notificationId`/`confirmationType` pair the whole slice identifies confirmations with.
 * `correlationId` lets the facade await this specific cleanup's outcome.
 */
export const cleanupCompletedPreviously = createAction(
    '[Confirmation] Cleanup completed previously',
    props<ConfirmationIdentityState & { correlationId: string }>(),
);

export const cleanupCompletedPreviouslySucceeded = createAction(
    '[Confirmation] Cleanup completed previously succeeded',
    props<ConfirmationIdentityState & { correlationId: string }>(),
);

export const cleanupCompletedPreviouslyFailed = createAction(
    '[Confirmation] Cleanup completed previously failed',
    props<ConfirmationIdentityState & { correlationId: string; error: string }>(),
);

/**
 * One-shot OData lookup of a measurement tool's calibration date. The outcome carries the raw
 * OData record (a plain interface, not a model) so the action stays serializable; the facade
 * converts it back to a `CalibrationModel`.
 */
export const calibrationDateRequested = createAction(
    '[Confirmation] Calibration date requested',
    props<{ measurementToolId: string; correlationId: string }>(),
);

export const calibrationDateSucceeded = createAction(
    '[Confirmation] Calibration date succeeded',
    props<{ correlationId: string; calibration: ODataCalibrationModel }>(),
);

export const calibrationDateFailed = createAction(
    '[Confirmation] Calibration date failed',
    props<{ correlationId: string; error: string }>(),
);
